import type { ChartOption } from '@app-types/chart';
import type { MetricChartPoint } from '@app-types/comparisonMetric';
import { CHART_AXIS_LABEL_STYLE, CHART_COLORS } from './chartConstants';

/** Colors used by MetricComparisonBarChart (see plan §7). */
export const METRIC_BAR_COLORS = {
  actual: '#e60000',
  forecast: '#e49697',
} as const;

interface AxisScale {
  min: number;
  max: number;
  interval: number;
}

const DEFAULT_AXIS_SCALE: AxisScale = { min: 0, max: 100, interval: 25 };
const NICE_STEPS = [1, 2, 2.5, 5, 10] as const;

const roundUpNice = (value: number): number => {
  if (!Number.isFinite(value) || value <= 0) return DEFAULT_AXIS_SCALE.max;

  const exponent = Math.floor(Math.log10(value));
  const base = 10 ** exponent;
  const normalized = value / base;
  const step = NICE_STEPS.find((candidate) => normalized <= candidate) ?? 10;
  return step * base;
};

export const buildMetricAxisScale = (points: readonly MetricChartPoint[]): AxisScale => {
  const values = points
    .map((point) => point.value)
    .filter((value) => Number.isFinite(value));

  if (values.length === 0 || values.every((value) => value === 0)) {
    return DEFAULT_AXIS_SCALE;
  }

  const minValue = Math.min(0, ...values);
  const maxValue = Math.max(0, ...values);

  if (minValue < 0 && maxValue > 0) {
    const bound = roundUpNice(Math.max(Math.abs(minValue), maxValue));
    return { min: -bound, max: bound, interval: bound / 2 };
  }

  if (minValue < 0) {
    const bound = roundUpNice(Math.abs(minValue));
    return { min: -bound, max: 0, interval: bound / 4 };
  }

  const max = roundUpNice(maxValue);
  return { min: 0, max, interval: max / 4 };
};

/**
 * Y-axis tick formatter: raw values are in yen at millions scale
 * (e.g. 2,617,626 means 2.6 million million yen per Figma table header
 * `百万円`). Displayed labels divide by 1,000 to match Figma text:
 * 10,000,000 → "10,000", 2,000,000 → "2,000", 0 → "0".
 */
const formatYAxis = (value: number): string =>
  value === 0 ? '0' : (value / 1000).toLocaleString('en-US');

/**
 * Build ECharts option for the metric comparison vertical bar chart.
 * See kiro_sales_revenue_task.md §7. Bars use actual color unless the
 * corresponding chart point has `forecast: true`.
 */
export function buildMetricComparisonOption(
  points: MetricChartPoint[]
): ChartOption {
  const axisScale = buildMetricAxisScale(points);

  return {
    grid: { left: 8, right: 8, top: 8, bottom: 8, containLabel: true },
    tooltip: { trigger: 'axis' },
    xAxis: {
      type: 'category',
      data: points.map((p) => p.label),
      axisTick: { show: false },
      axisLine: { lineStyle: { color: CHART_COLORS.grid } },
      axisLabel: {
        ...CHART_AXIS_LABEL_STYLE,
        rotate: -60,
        interval: 0,
      },
    },
    yAxis: {
      type: 'value',
      min: axisScale.min,
      max: axisScale.max,
      interval: axisScale.interval,
      axisLabel: { ...CHART_AXIS_LABEL_STYLE, formatter: formatYAxis },
      splitLine: { lineStyle: { color: CHART_COLORS.grid } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [
      {
        type: 'bar',
        data: points.map((p) => ({
          value: p.value,
          itemStyle: {
            color: p.forecast ? METRIC_BAR_COLORS.forecast : METRIC_BAR_COLORS.actual,
          },
        })),
        barWidth: '70%',
      },
    ],
  };
}
